/**
 * `.bru` files → IR pieces: a request file, `folder.bru`, `collection.bru`
 * and `environments/*.bru`.
 *
 * Fixes over the old single-file reader, each seen in real Bruno output:
 *  - query params live in `params:query` (and `params:path`); only our own
 *    exporter writes the legacy `query {}` block — both are read;
 *  - `auth:apikey` says `placement: header|queryparams` (legacy `in:` kept);
 *  - the method block's `auth:` and `body:` MODES decide, not whichever
 *    `auth:*` / `body:*` block happens to be present (Bruno keeps stale
 *    blocks: `auth: none` next to a leftover `auth:bearer` is common);
 *  - multipart, graphql, sparql and file bodies are read (file → warning).
 */
import {
  parseBruBlocks,
  indexBlocks,
  parseBruPairs,
  pairsToObject,
  parseBruList,
  textBlock,
  splitContentType,
  parseFileRef,
} from './bruLang';
import { authFromBrunoModel, emptyDefaults, emptyCounts, row } from './ir';

const METHOD_BLOCKS = ['get', 'post', 'put', 'patch', 'delete', 'head', 'options', 'trace', 'connect'];
const AUTH_BLOCK_ORDER = ['awsv4', 'basic', 'bearer', 'apikey', 'oauth2', 'digest', 'ntlm', 'wsse', 'oauth1', 'akamai-edgegrid'];

const rowsOf = (raw) => parseBruPairs(raw).map((r) => row(r.key, r.value, r.enabled));

function findMethod(byName) {
  for (const m of METHOD_BLOCKS) {
    if (byName[m] !== undefined) return { method: m.toUpperCase(), kv: pairsToObject(parseBruPairs(byName[m])) };
  }
  if (byName.http !== undefined) {
    const kv = pairsToObject(parseBruPairs(byName.http));
    return { method: String(kv.method || 'GET').toUpperCase(), kv };
  }
  return null;
}

/** Read one `auth:<mode>` block into Bruno's model, then into the IR. */
function readAuth(byName, mode) {
  const kv = (name) => pairsToObject(parseBruPairs(byName[`auth:${name}`] || ''));
  switch (mode) {
    case 'none':
    case 'inherit':
      return { mode };
    case 'bearer':
      return authFromBrunoModel({ mode, bearer: { token: kv('bearer').token } });
    case 'basic':
      return authFromBrunoModel({ mode, basic: kv('basic') });
    case 'apikey': {
      const a = kv('apikey');
      return authFromBrunoModel({ mode, apikey: { key: a.key, value: a.value, placement: a.placement || a.in } });
    }
    case 'awsv4':
      return authFromBrunoModel({ mode, awsv4: kv('awsv4') });
    case 'oauth2': {
      const o = kv('oauth2');
      return authFromBrunoModel({
        mode,
        oauth2: {
          grantType: o.grant_type,
          authorizationUrl: o.authorization_url,
          accessTokenUrl: o.access_token_url,
          callbackUrl: o.callback_url,
          clientId: o.client_id,
          clientSecret: o.client_secret,
          scope: o.scope,
          pkce: o.pkce,
        },
      });
    }
    default:
      return authFromBrunoModel({ mode });
  }
}

function inferAuthMode(byName) {
  for (const name of AUTH_BLOCK_ORDER) if (byName[`auth:${name}`] !== undefined) return name;
  return null;
}

function multipartRows(raw) {
  return parseBruPairs(raw).map((r) => {
    const { value } = splitContentType(r.value);
    const files = parseFileRef(value);
    if (files) return { key: r.key, value: '', enabled: r.enabled, type: 'file', src: files.join('|') };
    return { key: r.key, value, enabled: r.enabled, type: 'text' };
  });
}

const BODY_MODE_ALIASES = {
  json: 'json',
  text: 'text',
  xml: 'xml',
  sparql: 'sparql',
  formurlencoded: 'urlencoded',
  'form-urlencoded': 'urlencoded',
  multipartform: 'multipart',
  'multipart-form': 'multipart',
  graphql: 'graphql',
  file: 'file',
  none: 'none',
};

function inferBodyMode(byName) {
  const order = [
    ['body:json', 'json'], ['body:text', 'text'], ['body:xml', 'xml'], ['body:sparql', 'sparql'],
    ['body:form-urlencoded', 'urlencoded'], ['body:multipart-form', 'multipart'],
    ['body:graphql', 'graphql'], ['body:file', 'file'],
  ];
  for (const [block, mode] of order) if (byName[block] !== undefined) return mode;
  return 'none';
}

function readBody(byName, declared) {
  const mode = declared ? BODY_MODE_ALIASES[String(declared).toLowerCase()] || 'none' : inferBodyMode(byName);
  switch (mode) {
    case 'json':
    case 'text':
    case 'xml':
    case 'sparql':
      return { mode, text: textBlock(byName[`body:${mode}`]) };
    case 'urlencoded':
      return { mode, rows: rowsOf(byName['body:form-urlencoded']) };
    case 'multipart':
      return { mode, rows: multipartRows(byName['body:multipart-form']) };
    case 'graphql':
      return {
        mode,
        graphql: { query: textBlock(byName['body:graphql']), variables: textBlock(byName['body:graphql:vars']) },
      };
    case 'file':
      return { mode };
    default:
      return { mode: 'none' };
  }
}

function readScripts(byName) {
  return {
    pre: textBlock(byName['script:pre-request']),
    post: textBlock(byName['script:post-response']),
    tests: textBlock(byName.tests),
  };
}

const seqOf = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * A request `.bru` → IR request node. Never throws on odd content; a file
 * with no method block (gRPC / WebSocket) comes back `reqType:'unsupported'`.
 */
export function bruRequestToNode(text, fallbackName = '') {
  const { byName, counts } = indexBlocks(parseBruBlocks(text));
  const meta = pairsToObject(parseBruPairs(byName.meta));
  const name = meta.name || fallbackName || 'Untitled Request';
  const found = findMethod(byName);
  const type = String(meta.type || 'http').toLowerCase();

  if (!found || (type !== 'http' && type !== 'graphql')) {
    return { kind: 'request', name, seq: seqOf(meta.seq), reqType: 'unsupported', typeName: type };
  }

  const authMode = found.kv.auth || inferAuthMode(byName) || 'inherit';
  const query = byName['params:query'] !== undefined ? rowsOf(byName['params:query']) : rowsOf(byName.query);

  return {
    kind: 'request',
    name,
    seq: seqOf(meta.seq),
    reqType: type,
    method: found.method,
    url: found.kv.url || '',
    headers: rowsOf(byName.headers),
    query,
    pathParams: rowsOf(byName['params:path']),
    body: readBody(byName, found.kv.body),
    auth: readAuth(byName, String(authMode).toLowerCase()),
    scripts: readScripts(byName),
    docs: textBlock(byName.docs),
    counts: {
      ...emptyCounts(),
      vars: rowsOf(byName['vars:pre-request']).length + rowsOf(byName['vars:post-response']).length,
      assertions: rowsOf(byName.assert).length,
      examples: counts.example || 0,
    },
  };
}

/**
 * `folder.bru` / `collection.bru` → `{ name, seq, docs, defaults }`.
 * Folder/collection auth lives in an `auth { mode: … }` block; absent,
 * `none` or `inherit` all mean "not set here" (see toNormalized).
 */
export function bruDefaultsFromText(text) {
  const { byName } = indexBlocks(parseBruBlocks(text));
  const meta = pairsToObject(parseBruPairs(byName.meta));
  const defaults = emptyDefaults();
  defaults.headers = rowsOf(byName.headers);
  const mode = pairsToObject(parseBruPairs(byName.auth)).mode;
  if (mode) defaults.auth = readAuth(byName, String(mode).toLowerCase());
  defaults.vars = rowsOf(byName['vars:pre-request']);
  defaults.scripts = readScripts(byName);
  return { name: meta.name || '', seq: seqOf(meta.seq), docs: textBlock(byName.docs), defaults };
}

/** `environments/<name>.bru` → ImportedEnv. Secret names carry no value (O7). */
export function bruEnvFromText(text, name) {
  const { byName } = indexBlocks(parseBruBlocks(text));
  const variables = rowsOf(byName.vars).map((r) => ({ key: r.key, value: r.value, enabled: r.enabled, secret: false }));
  for (const s of parseBruList(byName['vars:secret'])) {
    if (s.name) variables.push({ key: s.name, value: '', enabled: s.enabled, secret: true });
  }
  return { name: name || 'Environment', variables: variables.filter((v) => v.key) };
}
